import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileUp } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

export const UploadGuide = () => {
  const nav = useNavigate();
  const ref = useRef(null);
  const [dest, setDest] = useState("");
  const [file, setFile] = useState(null);
  const [pct, setPct] = useState(null);
  const [existing, setExisting] = useState({ existing: [], existing_family: [] });
  const [family, setFamily] = useState(false);
  useEffect(() => { api.get("/relocation/destinations").then((r) => setExisting(r.data)).catch(() => {}); }, []);
  const taken = (family ? existing.existing_family : existing.existing).includes(dest.trim().toLowerCase());

  const go = async () => {
    setPct(0);
    try {
      const { data } = await api.post("/relocation/from-guide", file, {
        headers: { "Content-Type": "application/octet-stream", "x-filename": encodeURIComponent(file.name), "x-destination": encodeURIComponent(dest.trim()), "x-family": family ? "1" : "0" },
        onUploadProgress: (e) => e.total && setPct(Math.round((e.loaded / e.total) * 100)),
      });
      toast.success("Guide uploaded — the studio is splitting it into volumes and building the rest");
      nav(`/relocation/${data.id}`);
    } catch (e) { toast.error(errMsg(e)); setPct(null); }
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button data-testid="reloc-from-guide" variant="outline" className="rounded-none h-12 px-6 border-[#3B1B48] text-[#3B1B48]"><FileUp className="w-4 h-4 mr-2" />From a finished guide</Button>
      </DialogTrigger>
      <DialogContent className="rounded-none bg-[#FAF7F2] max-w-lg" data-testid="reloc-from-guide-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[#3B1B48]">Build from your complete guide</DialogTitle>
          <DialogDescription>Upload one complete guide as a PDF or HTML file. The studio finds its chapters, splits them into Volumes I–III, re-typesets all five PDFs in the studio style and creates the cover, illustrations, mobile app, listing and Instagram post.</DialogDescription>
        </DialogHeader>
        <Input data-testid="from-guide-destination" value={dest} onChange={(e) => setDest(e.target.value)} placeholder="Country (moving from Australia to…)" className="rounded-none bg-white h-11" />
<label className="flex items-center gap-2 text-sm text-[#3B1B48] cursor-pointer" data-testid="from-guide-family-toggle">
          <input type="checkbox" checked={family} onChange={(e) => setFamily(e.target.checked)} className="accent-[#3B1B48] w-4 h-4" />
          Families with children edition (written for parents moving with kids)
        </label>
        {taken && <p className="text-xs text-red-700" data-testid="from-guide-taken">There is already a {family ? "families " : ""}guide for {dest.trim()}.</p>}
        <input ref={ref} type="file" accept=".pdf,.html,.htm" className="hidden" data-testid="from-guide-file" onChange={(e) => setFile(e.target.files[0] || null)} />
        <button data-testid="from-guide-pick" onClick={() => ref.current.click()} className="border border-dashed border-[#C59B27] bg-white px-4 py-6 text-sm text-[#3B1B48] hover:bg-[#EFE6F3] transition-colors text-left">
          {file ? <><span className="font-medium">{file.name}</span><span className="block text-xs text-[#8C827A]">{(file.size / 1048576).toFixed(1)} MB · tap to change</span></> : "Choose the complete guide (PDF or HTML)"}
        </button>
        {pct !== null && <div className="h-1.5 bg-[#EFE6F3]"><div className="h-full bg-[#C59B27] transition-[width]" style={{ width: `${pct}%` }} data-testid="from-guide-progress" /></div>}
        <p className="text-xs text-[#8C827A]">Takes about 20–40 minutes and uses AI credit. The text must be selectable (not a scanned image). The guide stays a draft until you publish it.</p>
        <Button data-testid="from-guide-start" onClick={go} disabled={pct !== null || !file || dest.trim().length < 2 || taken} className="rounded-none h-11">{pct !== null ? (pct < 100 ? `Uploading ${pct}%` : "Reading the guide…") : "Upload and build"}</Button>
      </DialogContent>
    </Dialog>
  );
};
